import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema.js";
import { generateId } from "../../../../src/shared/schemas/WorldbuildingEntrySchema.js";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher";
import Auth, { isExpectedApiResponseError } from "../../Auth.js";
import { invalidateStoryBibleSummaryCaches, storyBibleCacheProjectIdsForEntities } from "../../lib/storyBibleSummaryCache.js";
import useClickedOutside from "../../hooks/useClickedOutside.js";
import { useRefetchOnFocus } from "../../hooks/useRefetchOnFocus";
import { useProjectStore } from "../../stores/projectStore.js";
import { useStoryBibleStore } from "../../stores/storyBibleStore.js";
import { useCrossAppSignalStore, CrossAppSignal } from "../../stores/crossAppSignalStore";
import { useToast } from "../ToastWrapper/index.js";
import {
  buildDerivedWorldbuildingRoles,
  buildWorldbuildingRoleOptions,
  cloneWorldbuildingFields,
  useWorldbuildingTemplates,
} from "./useWorldbuildingTemplates";
import { mergeCustomFieldsWithTemplateOrder } from "src/utils/mergeCustomFieldsWithTemplateOrder.js";

const NETWORK_BLOCKED_TOAST = "Your network is blocking Sudowrite requests. Try another network.";

export const useEntryState = (projectId, seriesId, collapsed, onToggleCollapse, onSetCollapsed) => {
  const [entries, setEntries] = useState([]);
  const entriesRef = useRef(entries);

  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false); // This is meant for external syncs that happen when you focus into the editor
  const [isCreating, setIsCreating] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const endOfListRef = useRef(null);
  const [isCustomizingDefaults, setIsCustomizingDefaultsInternal] = useState(false);
  const [isAddingDefaultFieldsToEntities, setIsAddingDefaultFieldsToEntities] = useState(false);
  const [updatedEntityNames, setUpdatedEntityNames] = useState([]);
  const [doneUpdatingEntityFields, setDoneUpdatingEntityFields] = useState(false);
  const [showGenerateGuidance, setShowGenerateGuidance] = useState(false);
  const [showGenerateGuidanceBottom, setShowGenerateGuidanceBottom] = useState(false);
  const [_generatingEntry, setGeneratingEntry] = useState(false);
  const isAgentGeneratingWorldbuilding = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.AGENTIC_WORLDBUILDING_RUNNING]);
  const generatingEntry = _generatingEntry || isAgentGeneratingWorldbuilding;
  const [guidance, setGuidance] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const guidanceRef = useClickedOutside(() => setShowGenerateGuidance(false));
  const guidanceBottomRef = useClickedOutside(() => setShowGenerateGuidanceBottom(false));
  const refreshTrashedItems = useProjectStore((state) => state.refreshTrashedItems);
  const seed = useStoryBibleStore((state) => state.seed);
  const synopsis = useStoryBibleStore((state) => state.synopsis);
  const seedRef = useRef(seed);
  const synopsisRef = useRef(synopsis);
  const refreshWorldbuildingEntries = useStoryBibleStore((state) => state.refreshWorldbuildingEntries);
  const upsertWorldbuildingEntity = useStoryBibleStore((state) => state.upsertWorldbuildingEntity);
  const clearWorldbuildingDiff = useStoryBibleStore((state) => state.clearWorldbuildingDiff);
  const worldbuildingEntries = useStoryBibleStore((state) => state.worldbuildingEntries);
  const worldbuildingEntriesProjectId = useStoryBibleStore((state) => state.worldbuildingEntriesProjectId);
  const entriesLoading = useStoryBibleStore((state) => !!state.refreshingWorldbuildingByProjectId[projectId]);
  const entriesLoadFailed = useStoryBibleStore((state) => !!state.worldbuildingLoadFailuresByProjectId[projectId]);
  const worldbuildingRefreshTrigger = useStoryBibleStore((state) => state.worldbuildingRefreshTrigger);
  const lastSyncedWorldbuildingRefreshTriggerRef = useRef(null);
  const { setToast } = useToast();
  const {
    templates: worldbuildingTemplates,
    customTemplates: customDefaults,
    roleOptions: guidanceRoleOptions,
    refreshTemplates: loadCustomDefaults,
    getTemplateForRole,
  } = useWorldbuildingTemplates();
  const [guidanceRoleOption, setGuidanceRoleOption] = useState(null);
  const [showGenerateElementForm, setShowGenerateElementForm] = useState(false);
  const generateElementFormRef = useRef(null);
  const prevAgentGeneratingWb = useRef(false);
  const agentTriggeredForm = useRef(false);
  useEffect(() => {
    if (isAgentGeneratingWorldbuilding && !prevAgentGeneratingWb.current) {
      onSetCollapsed(false);
      setShowGenerateElementForm(true);
      agentTriggeredForm.current = true;
      setTimeout(() => {
        generateElementFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    } else if (!isAgentGeneratingWorldbuilding && prevAgentGeneratingWb.current && agentTriggeredForm.current) {
      setShowGenerateElementForm(false);
      agentTriggeredForm.current = false;
    }
    prevAgentGeneratingWb.current = !!isAgentGeneratingWorldbuilding;
  }, [isAgentGeneratingWorldbuilding, onSetCollapsed]);

  // Ref to track the latest toggle request to prevent race conditions
  const latestToggleRequestRef = useRef(0);

  const updateEntries = (newEntries) => {
    if (!Array.isArray(newEntries)) {
      Sentry.captureMessage("useEntryState.updateEntries received non-array input", {
        level: "warning",
        extra: { newEntriesType: typeof newEntries, projectId },
      });
      newEntries = [];
    }

    const parsed = newEntries
      .filter(Boolean)
      .map((e) => ({ ...e, displayOrder: parseFloat(e.displayOrder) }));
    const sorted = parsed.sort((a, b) => a.displayOrder - b.displayOrder);
    entriesRef.current = sorted;
    setEntries(sorted);
  };

  const loadEntries = useCallback(
    async (external) => {
      try {
        if (external) {
          setSyncing(true);
        }
        await refreshWorldbuildingEntries(
          projectId,
          `Worldbuilding.useEntryState.loadEntries:${external ? "external" : "initial"}`,
          { force: external },
        );
      } catch (e) {
        if (e?.isNetworkPolicyBlocked) {
          setToast(NETWORK_BLOCKED_TOAST);
          return;
        }
        if (!isExpectedApiResponseError(e)) {
          Sentry.captureException(e, { tags: { projectId } });
        }
        setToast("Failed to load entries");
      } finally {
        if (external) {
          setSyncing(false);
        }
      }
    },
    [projectId, refreshWorldbuildingEntries, setToast],
  );

  useEffect(() => {
    seedRef.current = seed;
  }, [seed]);

  useEffect(() => {
    synopsisRef.current = synopsis;
  }, [synopsis]);

  const loadEntriesRef = useRef(loadEntries);

  useEffect(() => {
    loadEntriesRef.current = loadEntries;
  }, [loadEntries]);

  useRefetchOnFocus(loadEntries, [projectId, loadEntries], { skipInitialFetch: true });

  useEffect(() => {
    setInitialLoaded(false);
    lastSyncedWorldbuildingRefreshTriggerRef.current = null;
    entriesRef.current = [];
    setEntries([]);
  }, [projectId]);

  useEffect(() => {
    if (worldbuildingEntriesProjectId !== projectId) return;
    if (worldbuildingRefreshTrigger === lastSyncedWorldbuildingRefreshTriggerRef.current) return;
    lastSyncedWorldbuildingRefreshTriggerRef.current = worldbuildingRefreshTrigger;
    updateEntries(worldbuildingEntries);
    setInitialLoaded(true);
  }, [projectId, worldbuildingRefreshTrigger, worldbuildingEntries, worldbuildingEntriesProjectId]);

  useEffect(() => {
    if (!guidanceRoleOptions.length) {
      setGuidanceRoleOption(null);
      return;
    }

    setGuidanceRoleOption((currentOption) => {
      if (!currentOption) return guidanceRoleOptions[0];
      return guidanceRoleOptions.find((option) => option.value === currentOption.value) || guidanceRoleOptions[0];
    });
  }, [guidanceRoleOptions]);

  const reorderEntries = async (draggingId, overId, isStart) => {
    const startingEntries = entries.map((e) => ({ ...e }));
    if (draggingId === overId) return;
    AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.Reorder);
    const draggedIndex = entries.findIndex((e) => e.id === draggingId);
    const draggedEntry = entries[draggedIndex];
    const overIndex = entries.findIndex((e) => e.id === overId);
    const overEntry = entries[overIndex];

    const updatedEntry = { ...draggedEntry };
    if (overIndex === entries.length - 1) {
      updatedEntry.displayOrder = overEntry.displayOrder + 1;
    } else if (isStart) {
      if (draggedIndex === 0) {
        return;
      }
      const mininumDisplayOrder = entries[0].displayOrder;
      updatedEntry.displayOrder = mininumDisplayOrder - 1;
    } else {
      if (overIndex === draggedIndex - 1) {
        // it's staying in the same place
        return;
      }
      const neighborIndex = overIndex + 1;
      const neighbor = entries[neighborIndex];
      const displayOrderSpace = Math.abs(neighbor.displayOrder - overEntry.displayOrder);
      // just choose a random number between the two characters
      const newDisplayOrder = Math.random() * displayOrderSpace + Math.min(overEntry.displayOrder, neighbor.displayOrder);
      updatedEntry.displayOrder = newDisplayOrder;
    }

    updateEntries(entries.map((e) => (e.id === draggingId ? updatedEntry : e)));
    try {
      const result = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${draggingId}`, {
        method: "PUT",
        entry: updatedEntry,
        updatedAt: updatedEntry.updatedAt,
        projectId,
      });
      updateEntries(entries.map((e) => (e.id === draggingId ? result.entry : e)));
    } catch (e) {
      Sentry.captureException(e, { tags: { projectId } });
      setToast("Failed to reorder entry");
      updateEntries(startingEntries);
    }
  };

  const handleDragStart = ({ active }) => {
    if (!active) return;
    setDraggingId(active.id);
  };

  const handleDragEnd = async ({ over }) => {
    setDraggingId(null);
    if (over) {
      reorderEntries(draggingId, over.id, over.data?.current?.start);
    }
  };

  const handleDragCancel = () => {
    setDraggingId(null);
  };

  const handeCreateBlankEntry = async () => {
    await createEntryFromRole("other");
  };

  const getFieldsForRole = (roleValue) => {
    const template = getTemplateForRole(roleValue);
    if (template?.defaultFields?.length) {
      return cloneWorldbuildingFields(template.defaultFields);
    }
    return [{ id: generateId(15), label: "Description" }];
  };

  const createEntryFromRole = async (roleValue, position) => {
    const customFields = getFieldsForRole(roleValue);
    AppEventPublisher.storyBible.worldbuilding.addBlankElement(roleValue, position);
    try {
      setLoading(true);
      setIsCreating(true);
      const maxDisplayOrder = Math.max(...entries.map((e) => e.displayOrder), 0);
      const result = await Auth.fetchWithIdToken("/api/worldbuildingEntries/", {
        displayOrder: maxDisplayOrder + 1,
        collapsed: false,
        role: roleValue,
        customFields,
        projectId,
      });

      updateEntries([...entries, result.entry]);
      upsertWorldbuildingEntity(result.entry);

      onSetCollapsed(false); // force uncollapse so scrolling works
      setTimeout(() => {
        endOfListRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } catch (e) {
      Sentry.captureException(e, { tags: { projectId } });
      setToast("Failed to create entry");
    } finally {
      setLoading(false);
      setIsCreating(false);
      refreshWorldbuildingEntries(projectId, "Worldbuilding.useEntryState.createEntryFromRole.finally", { force: true });
    }
  };

  const restoreSnapshot = useCallback(
    async (entryId, snapshotId) => {
      try {
        setLoading(true);
        setIsRestoring(true);
        const result = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entryId}/restoreSnapshot`, {
          method: "POST",
          snapshotId,
          projectId,
        });
        AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.RestoreHistory);
        setToast("Snapshot restored");

        updateEntries(entriesRef.current.map((e) => (e.id === entryId ? result.entry : e)));
        clearWorldbuildingDiff(entryId);
      } catch (e) {
        Sentry.captureException(e, { tags: { projectId } });
        setToast("Failed to restore snapshot");
      } finally {
        setLoading(false);
        setIsRestoring(false);
      }
    },
    [projectId, clearWorldbuildingDiff, setToast]
  );

  const handleDeleteEntries = useCallback(
    async (entryIds) => {
      setLoading(true);
      if (entryIds.length === 0) return;
      try {
        const currentEntries = entriesRef.current;
        const entriesToDelete = currentEntries.filter((e) => entryIds.includes(e.id));
        updateEntries(currentEntries.filter((e) => !entryIds.includes(e.id)));
        const deletedEntries = [];

        const deletionPromises = entriesToDelete.map((entry) =>
          Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entry.id}`, {
            method: "DELETE",
            projectId,
          }).then(() => {
            AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.Delete);
            const entryName = extractTextFromTipTap(entry.name);
            deletedEntries.push({ id: entry.id, name: entryName });
          })
        );

        await Promise.all(deletionPromises);

        const restoreAll = async () => {
          if (deletedEntries.length === 0) return;
          try {
            setLoading(true);
            setIsRestoring(true);

            await Promise.all(
              deletedEntries.map((entry) =>
                Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entry.id}/restore`, {
                  method: "POST",
                  projectId,
                })
              )
            );

            await loadEntriesRef.current();

            deletedEntries.length > 1
              ? setToast(`${deletedEntries.length} entries restored`)
              : setToast(`Entry ${deletedEntries[0].name} restored`);
          } catch (e) {
            Sentry.captureException(e, { tags: { projectId } });
            setToast("Failed to restore entries");
          } finally {
            setLoading(false);
            setIsRestoring(false);
          }
        };

        const message =
          deletedEntries.length === 1 ? (
            <>
              Entry {deletedEntries[0].name ? `"${deletedEntries[0].name}"` : ""} deleted. <a onClick={restoreAll}>Undo</a>
            </>
          ) : (
            <>
              {deletedEntries.length} entries deleted. <a onClick={restoreAll}>Undo</a>
            </>
          );

        setToast(message);
      } catch (e) {
        Sentry.captureException(e, { tags: { projectId } });
        setToast(entryIds.length === 1 ? "Failed to delete entry" : "Failed to delete entries");
      } finally {
        setLoading(false);
        refreshTrashedItems();
        refreshWorldbuildingEntries(projectId, "Worldbuilding.useEntryState.handleDeleteEntries.finally", { force: true });
      }
    },
    [projectId, refreshTrashedItems, refreshWorldbuildingEntries, setToast]
  );

  const handleSaveEntry = useCallback(
    (entry) => {
      const currentEntries = entriesRef.current;
      const latestCollapsed = currentEntries.find((e) => e.id === entry.id)?.collapsed;
      const entryWithSyncedCollapsed =
        typeof latestCollapsed === "boolean" && latestCollapsed !== entry.collapsed ? { ...entry, collapsed: latestCollapsed } : entry;

      updateEntries(currentEntries.map((e) => (e.id === entry.id ? entryWithSyncedCollapsed : e)));
    },
    []
  );

  const toggleEntryCollapsed = useCallback(
    async (entryId, localEntryData) => {
      const currentEntries = entriesRef.current;
      const entry = localEntryData || currentEntries.find((e) => e.id === entryId);
      const updatedEntry = { ...entry, collapsed: !entry.collapsed };
      AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.ToggleCollapse);
      // Generate a unique request ID and store it as the latest
      const requestId = ++latestToggleRequestRef.current;

      updateEntries(currentEntries.map((e) => (e.id === entryId ? updatedEntry : e)));
      try {
        setLoading(true);
        const result = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entryId}`, {
          method: "PUT",
          entry: updatedEntry,
          updatedAt: updatedEntry.updatedAt,
          projectId,
        });

        // Only update entries if this is still the latest request
        if (requestId === latestToggleRequestRef.current) {
          updateEntries(entriesRef.current.map((e) => (e.id === entryId ? result.entry : e)));
        }
      } catch (e) {
        Sentry.captureException(e, { tags: { projectId } });
          setToast("Failed to save entry");
        // Only revert if this is still the latest request
        if (requestId === latestToggleRequestRef.current) {
          updateEntries(entriesRef.current.map((e) => (e.id === entryId ? entry : e)));
        }
      } finally {
        setLoading(false);
      }
    },
    [projectId, setToast]
  );

  const toggleIsCustomizingDefaults = (value) => {
    setIsCustomizingDefaultsInternal(value);
    setDoneUpdatingEntityFields(false);
    setUpdatedEntityNames([]);
  };

  const addFieldsToEntities = async (fields, role) => {
    setIsAddingDefaultFieldsToEntities(true);
    try {
      const entitiesToUpdate = entries.filter((e) => e.role === role);
      const results = await Promise.allSettled(
        entitiesToUpdate.map(async (entity) => {
          const mergedFields = mergeCustomFieldsWithTemplateOrder(
            entity.customFields,
            fields,
            (tf) => cloneWorldbuildingFields([tf])[0],
          );
          if (isEqual(mergedFields, entity.customFields)) {
            return entity;
          }
          const result = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entity.id}`, {
            method: "PUT",
            entry: { ...entity, customFields: mergedFields },
            updatedAt: entity.updatedAt,
            projectId,
            deferSummaryCacheInvalidation: true,
          });
          setUpdatedEntityNames((prev) => [...prev, extractTextFromTipTap(result.entry.name)]);
          return result.entry;
        }),
      );
      const updatedEntities = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      if (updatedEntities.length > 0) {
        await invalidateStoryBibleSummaryCaches(
          storyBibleCacheProjectIdsForEntities(entitiesToUpdate, projectId),
          ["worldbuilding"],
        );
      }

      if (results.some((result) => result.status === "rejected")) {
        throw results.find((result) => result.status === "rejected").reason;
      }

      updateEntries(entries.map((e) => updatedEntities.find((ue) => ue.id === e.id) || e));
      setDoneUpdatingEntityFields(true);
    } catch (e) {
      setToast("Failed to add fields to entities");
      Sentry.captureException(e, { tags: { projectId } });
      console.error("Failed to add fields to entities", e);
    } finally {
      setIsAddingDefaultFieldsToEntities(false);
    }
  };

  const generateEntry = async (guidance, role) => {
    try {
      setGeneratingEntry(true);
      AppEventPublisher.storyBible.generate(StoryBibleType.worldbuilding);
      const displayOrder = Math.max(...entries.map((e) => e.displayOrder), 0) + 1;
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/worldbuildingEntry", {
        guidance,
        role,
        fields: getFieldsForRole(role),
        displayOrder,
        projectId,
      });
      updateEntries([...entries, result.entry]);
      upsertWorldbuildingEntity(result.entry);
      setShowGenerateElementForm(false);

      onSetCollapsed(false); // force uncollapse so scrolling works
      setTimeout(() => {
        endOfListRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } catch (e) {
      Sentry.captureException(e, { tags: { projectId } });
      setToast("Failed to generate entry");
    } finally {
      setGeneratingEntry(false);
      refreshWorldbuildingEntries(projectId, "Worldbuilding.useEntryState.generateEntry.finally", { force: true });
    }
  };

  const regenerateTrait = useCallback(
    async (elementId, traitId, traitLabel, guidance, isEmpty) => {
      try {
        AppEventPublisher.storyBible.worldbuilding.generateTrait(isEmpty);
        const response = await Auth.fetchWithIdToken(`/api/ai/storyEngine/regenerateWorldbuildingTrait`, {
          method: "POST",
          projectId,
          traitId,
          trait: traitLabel, // Keep for backward compatibility
          seed: seedRef.current,
          synopsis: synopsisRef.current,
          elementId,
          guidance,
        });
        updateEntries(entriesRef.current.map((e) => (e.id === elementId ? response.element : e)));
      } catch (e) {
        setToast("Failed to regenerate trait");
      }
    },
    [projectId, setToast]
  );

  const handleGenerateRequested = () => {
    AppEventPublisher.storyBible.worldbuilding.openGenerate("top");
    setShowGenerateGuidance(true);
  };
  const handleGenerateRequestedBottom = () => {
    AppEventPublisher.storyBible.worldbuilding.openGenerate("bottom");
    setShowGenerateGuidanceBottom(true);
  };

  const handleGenerateSubmitted = (e) => {
    e.preventDefault();
    setShowGenerateGuidance(false);
    setShowGenerateGuidanceBottom(false);
    generateEntry(guidance, guidanceRoleOption?.value || guidanceRoleOptions[0]?.value || "sudo-custom");
    setGuidance("");
  };
  const handleRandomGenerate = (e) => {
    e.preventDefault();
    setGuidance("");
    setShowGenerateGuidance(false);
    setShowGenerateGuidanceBottom(false);
    generateEntry("");
  };

  const handleRandomGenerateSimplified = () => {
    if (!guidanceRoleOptions.length) return;
    const randomRole = guidanceRoleOptions[Math.floor(Math.random() * guidanceRoleOptions.length)];
    setShowGenerateElementForm(true);
    generateElementFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    generateEntry("", randomRole.value);
  };

  const handleGuidanceKeys = (e) => {
    if (e.key === "Escape") {
      setShowGenerateGuidance(false);
      setShowGenerateGuidanceBottom(false);
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleGenerateSubmitted(e);
    }
  };

  const handleGuidanceRoleChange = useCallback(
    (option) => {
      setGuidanceRoleOption(option ?? (guidanceRoleOptions[0] ?? null));
    },
    [guidanceRoleOptions]
  );

  const derivedRoles = useMemo(() => buildDerivedWorldbuildingRoles(entries, worldbuildingTemplates), [entries, worldbuildingTemplates]);

  const entryRoleOptions = useMemo(
    () => buildWorldbuildingRoleOptions(worldbuildingTemplates, derivedRoles),
    [worldbuildingTemplates, derivedRoles]
  );

  return {
    addFieldsToEntities,
    isAddingDefaultFieldsToEntities,
    createEntryFromRole,
    customDefaults,
    draggingId,
    derivedRoles,
    entryRoleOptions,
    endOfListRef,
    entries,
    entriesLoadFailed,
    entriesLoading,
    generatingEntry,
    guidanceRef,
    guidance,
    guidanceRoleOption,
    guidanceRoleOptions,
    handeCreateBlankEntry,
    handleGenerateSubmitted,
    handleGuidanceRoleChange,
    handleGuidanceKeys,
    handleRandomGenerate,
    handleDeleteEntries,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    handleGenerateRequested,
    handleSaveEntry,
    initialLoaded,
    isCustomizingDefaults,
    isCreating,
    isDragging: draggingId !== null,
    isRestoring,
    loading,
    loadEntries,
    loadCustomDefaults,
    restoreSnapshot,
    setGuidance,
    showGenerateGuidance,
    toggleEntryCollapsed,
    toggleIsCustomizingDefaults,
    updatedEntityNames,
    doneUpdatingEntityFields,
    showGenerateGuidanceBottom,
    handleGenerateRequestedBottom,
    guidanceBottomRef,
    regenerateTrait,
    updateEntries,
    showGenerateElementForm,
    setShowGenerateElementForm,
    handleRandomGenerateSimplified,
    generateElementFormRef,
    syncing,
  };
};
